import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { PrimaryButton, SecondaryButton } from '../../../../../../CommonComponents/Button';
import ErrorBubble from '../../../../../../CommonComponents/ErrorBubble';
import FilePicker from '../../../../../../CommonComponents/Filepicker/FilepickerView';
import getImagePath from '../../../../../../CommonUtils/getImagePath';
import useApi from '../../../../../../_hooks/useApi';
import { setAssetClasses, setReturns, setVolatilities, setCorrelations } from '../../../actions';
import { Error as ErrorMessage, Success } from '../../../styles';
import { AssetClass, Correlations, Returns, Volatilities } from '../../../types';
import { BottomNavigation, SectionTitle, StepBody, StepContent } from '../styles';
import { generateCorrelationMatrixCsv, parseCorrelationMatrixCsv } from '../utils';
import { downloadFile } from '../../../../../../CommonUtils/downloadFile';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';

const Instructions = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  margin-bottom: 32px;

  li + li {
    margin-top: 24px;
  }

  a {
    color: #18a0fb;

    &:hover {
      color: #18a0fb;
    }
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  > * + * {
    margin-left: 16px;
  }
`;

interface ImportCorrelationMatrixProps {
  assetClasses: AssetClass[];
  returns: Returns;
  volatilities: Volatilities;
  next: () => void;
}

export const ImportCorrelationMatrix: React.FC<ImportCorrelationMatrixProps> = ({
  assetClasses,
  returns,
  volatilities,
  next,
}) => {
  const dispatch = useDispatch();

  const [localCorrelations, setLocalCorrelations] = useState<Correlations | undefined>(undefined);
  const [localErrors, setLocalErrors] = useState<string[]>([]);

  const [validate, { loading: validating, errors: validationErrors }] = useApi(
    '/Adviser/MarketDataAssumptions/ValidateMarketDataAssumptions',
    {
      method: 'POST',
    },
  );

  const [save, { loading: saving, errors: saveErrors }] = useApi(
    '/Adviser/MarketDataAssumptions/SaveMarketDataAssumptions',
    {
      method: 'POST',
    },
  );

  const handleValidate = useCallback(
    async matrixFile => {
      try {
        setLocalErrors([]);

        const parsedCorrelations: any = await parseCorrelationMatrixCsv(matrixFile, assetClasses);

        validate({
          body: { assetClasses, returns, volatilities, correlations: parsedCorrelations },
          onSuccess() {
            setLocalCorrelations(parsedCorrelations);
          },
        });
      } catch (err) {
        if (err instanceof Error) {
          setLocalErrors([err.message]);
        }
      }
    },
    [validate, assetClasses, returns, volatilities],
  );

  const templateCsv = new Blob([generateCorrelationMatrixCsv(assetClasses)], { type: 'text/csv' });

  return (
    <StepBody>
      <StepContent>
        <Alert
          variant="warning"
          title="Setting Changed"
          message="Clicking finish will change the capital market assumptions for all plans within your organization."
          mb="16"
        />

        <ErrorBubble show={saveErrors.length > 0} errors={saveErrors} />

        <SectionTitle>Correlation Matrix</SectionTitle>
        <Instructions>
          <li>1. Download your custom CSV template file</li>
          <li>
            2. Without changing the structure, populate the CSV file with your custom correlation
            values
          </li>
          <li>
            3. The correlation matrix must be a valid positive definite matrix (
            <a href={getImagePath('Matrix-sample.csv')} download="Matrix-sample.csv">
              see sample CSV
            </a>
            )
          </li>
          <li>4. Lastly, import your populated CSV file and select &quot;Finish&quot;</li>
        </Instructions>

        <Actions>
          <SecondaryButton
            prependIcon="fas fa-download"
            onClick={() => {
              const fileBlob = templateCsv;
              const fileName = 'Matrix-template.csv';

              downloadFile(fileName, fileBlob);
            }}
          >
            DOWNLOAD TEMPLATE
          </SecondaryButton>
          <FilePicker
            accept=".csv"
            onClick={ev => {
              ev.target.value = null;
            }}
            onChange={async ([file]) => {
              handleValidate(file);
            }}
          >
            <PrimaryButton disabled={validating} prependIcon="fas fa-cloud-upload-alt">
              IMPORT CSV
            </PrimaryButton>
          </FilePicker>
        </Actions>

        {[...validationErrors, ...localErrors].map(error => (
          <ErrorMessage key={error}>{error}</ErrorMessage>
        ))}

        {localCorrelations && <Success>The correlation matrix was successfully imported</Success>}
      </StepContent>
      <BottomNavigation>
        <PrimaryButton
          onClick={() =>
            save({
              body: { assetClasses, returns, volatilities, correlations: localCorrelations },
              onSuccess: () => {
                dispatch(setReturns(returns));
                dispatch(setVolatilities(volatilities));
                dispatch(setAssetClasses(assetClasses));
                if (localCorrelations) {
                  dispatch(setCorrelations(localCorrelations));
                }
                next();
              },
            })
          }
          disabled={saving || !localCorrelations}
          isLoading={saving}
        >
          Finish
        </PrimaryButton>
      </BottomNavigation>
    </StepBody>
  );
};
